import React, { useState, useEffect } from "react";
import { State } from "../../state/state";
import { IReducer } from "../../state/reducer";
import { AchievementInfo } from "../../state/Achievements/AchievementInfoTypes";
import { AchievementInfoTotal } from "../../state/Achievements/AchievementInfoTotal";
import AchievementCard from "../AchievementCard/AchievementCard";
import "./AchievementsView.css";

interface AchievementsViewProps extends IReducer {}

type CategoryType = "all" | "brick" | "action" | "brickpack" | "set" | "total";

const renderProgress = (state: State) => {
  let unlocked = state.unlockedAchievements.size;
  let total = AchievementInfoTotal.size;

  return `${unlocked}/${total}`;
};

const AchievementsView: React.FC<AchievementsViewProps> = ({
  state,
  dispatch,
}) => {
  const [visibleAchievements, setVisibleAchievements] = useState<
    AchievementInfo[]
  >([]);
  const [activeCategory, setActiveCategory] = useState<CategoryType>("all");
  const [hideUnlocked, setHideUnlocked] = useState(false);
  const [hideUnstarted, setHideUnstarted] = useState(false);
  const [activeSearch, setActiveSearch] = useState<string>("");

  // Helper function to get current progress for any achievement
  const getCurrentProgress = (achievement: AchievementInfo): number => {
    switch (achievement.category) {
      case "total":
        if (achievement.id.includes("total_bricks")) {
          return state.totalBricksCollected;
        }
        if (achievement.id.includes("total_actions")) {
          return state.totalActionsTriggered;
        }
        if (achievement.id.includes("total_sets")) {
          return state.totalSetsPurchased;
        }
        return 0;

      case "brick":
        return state.bricksCollectedByType.get(achievement.targetId || "") || 0;

      case "action":
        return (
          state.actionsTriggeredByType.get(achievement.targetId || "") || 0
        );

      case "brickpack":
        return state.brickPackStats.get(achievement.targetId || "") || 0;

      case "set":
        return state.setsPurchasedByType.get(achievement.targetId || "") || 0;

      default:
        return 0;
    }
  };

  useEffect(() => {
    let filteredAchievements: AchievementInfo[] = [];

    AchievementInfoTotal.forEach((achievement) => {
      // Filter by category
      if (activeCategory !== "all" && achievement.category !== activeCategory) {
        return;
      }

      // Filter by unlocked status
      if (hideUnlocked && state.unlockedAchievements.has(achievement.id)) {
        return;
      }

      // Filter by unstarted status (no progress made)
      if (hideUnstarted && getCurrentProgress(achievement) === 0) {
        return;
      }

      // Filter by search
      if (activeSearch.trim() !== "") {
        const searchLower = activeSearch.toLowerCase();
        const matchesTitle = achievement.title
          .toLowerCase()
          .includes(searchLower);
        const matchesDescription = achievement.description
          .toLowerCase()
          .includes(searchLower);
        if (!matchesTitle && !matchesDescription) {
          return;
        }
      }

      filteredAchievements.push(achievement);
    });

    // Sort by threshold (lower first)
    filteredAchievements.sort((a, b) => a.threshold - b.threshold);

    setVisibleAchievements(filteredAchievements);
  }, [
    activeCategory,
    hideUnlocked,
    hideUnstarted,
    activeSearch,
    state.unlockedAchievements,
    state.totalBricksCollected,
    state.totalActionsTriggered,
    state.totalSetsPurchased,
    state.bricksCollectedByType,
    state.actionsTriggeredByType,
    state.brickPackStats,
    state.setsPurchasedByType,
  ]);

  const handleCategoryClick = (category: CategoryType) => {
    setActiveCategory(category);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setActiveSearch(event.target.value);
  };

  const handleHideUnlockedChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setHideUnlocked(event.target.checked);
  };

  const handleHideUnstartedChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setHideUnstarted(event.target.checked);
  };

  return (
    <div className="achievementsViewWrapper">
      <div className="achievementsViewContainer">
        <div className="achievementsViewHeader">Achievements</div>
        <div className="achievementsViewContent">
          <div className="achievementsContainer">
            <div className="achievementsTabs">
              <button
                className={`achievementTab ${activeCategory === "all" ? "achievementTabActive" : ""}`}
                onClick={() => handleCategoryClick("all")}
              >
                All
              </button>
              <button
                className={`achievementTab ${activeCategory === "brick" ? "achievementTabActive" : ""}`}
                onClick={() => handleCategoryClick("brick")}
              >
                Bricks
              </button>
              <button
                className={`achievementTab ${activeCategory === "brickpack" ? "achievementTabActive" : ""}`}
                onClick={() => handleCategoryClick("brickpack")}
              >
                Brick Packs
              </button>
              <button
                className={`achievementTab ${activeCategory === "set" ? "achievementTabActive" : ""}`}
                onClick={() => handleCategoryClick("set")}
              >
                Sets
              </button>
              <button
                className={`achievementTab ${activeCategory === "total" ? "achievementTabActive" : ""}`}
                onClick={() => handleCategoryClick("total")}
              >
                Totals
              </button>
            </div>

            <div className="achievementsFilters">
              <div className="achievementsSearchContainer">
                <input
                  type="text"
                  className="searchInput achievementsSearchInput"
                  onChange={handleSearchChange}
                  placeholder="Search"
                  value={activeSearch}
                />
              </div>
              <div className="achievementsCheckboxContainer">
                <input
                  className="achievementsCheckbox"
                  type="checkbox"
                  checked={hideUnlocked}
                  onChange={handleHideUnlockedChange}
                />
                <span className="achievementsCheckboxLabel">Hide unlocked</span>
              </div>
              <div className="achievementsCheckboxContainer">
                <input
                  className="achievementsCheckbox"
                  type="checkbox"
                  checked={hideUnstarted}
                  onChange={handleHideUnstartedChange}
                />
                <span className="achievementsCheckboxLabel">
                  Hide unstarted
                </span>
              </div>
            </div>

            <div className="achievementsProgress">
              Progress: {renderProgress(state)}
            </div>

            <div className="achievementsItems">
              {visibleAchievements.map((achievement) => (
                <AchievementCard
                  key={achievement.id}
                  state={state}
                  achievement={achievement}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AchievementsView;
